import type { Resident } from '@lmc/ts-common'
import { Warning } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import ExternalLink from 'components/icons/ExternalLink'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { trim } from 'lodash'
import { type FunctionComponent, Suspense } from 'react'
import { Link } from 'react-router-dom'
import Asterix from 'screens/Rostering/RotaPattern/components/Asterix'
import Field from '../../fields'
import Modal from '..'
import PopupModal from '../PopupModal'

interface Medication {
  id: string
  name: string
}

interface EmarDiscontinueMedModalProps {
  emarV2ChartEnabled?: boolean
  fields: any
  isOpen: boolean
  medication: Medication
  resident: Resident
  showToast: (props: any) => void
  updateItem: (props: any) => void
}

const handleComplete = (props: any) => {
  const { emarV2ChartEnabled, fetchList, showToast } = props

  fetchList(
    'emar-resident-medications',
    undefined,
    emarV2ChartEnabled
      ? '/emar/medication-schedule-patterns/legacy/emar-resident-medications'
      : undefined
  )
  showToast({ title: 'Discontinued a Medication' })
}

const handleError = (props: any) => {
  const { error, showToast } = props

  console.log(error)
  showToast({ type: 'error', title: 'Something went wrong...' })
}

const handleSubmit = (props: EmarDiscontinueMedModalProps) => {
  const { emarV2ChartEnabled, fields, medication, updateItem } = props

  updateItem({
    url: emarV2ChartEnabled
      ? `/emar/medication-schedule-patterns/legacy/emar-resident-medications/${medication.id}`
      : `list/emar-resident-medications/${medication.id}`,
    json: {
      deleted: true,
      deletedOn: dayjs().toISOString(),
      reasonDeleted: fields.reasonDeleted
    },
    onComplete: () => handleComplete(props),
    onError: (error: any) => handleError({ ...props, error })
  })
}

/**
 * Renders modal for discontinuing a medication
 *
 * @param {object} medication - The medication to be discontinued.
 * @param {object} resident - The resident that the medication is assigned to.
 *
 * @returns {FunctionComponent<EmarDiscontinueMedModalProps>} EmarDiscontinueMedModal
 */
const EmarDiscontinueMedModal: FunctionComponent<
  EmarDiscontinueMedModalProps
> = (props) => {
  const flags = useFeatureFlags({ flagNames: ['emar_v2_chart'] })?.data
  const emarV2ChartEnabled = flags?.emar_v2_chart?.enabled ?? false
  const modalProps = { ...props, emarV2ChartEnabled }
  const { fields, isOpen, medication, resident, ...formProps } = props

  if (!isOpen) return null

  const supportLinkText =
    'https://support.logmycare.co.uk/en/articles/4851811-how-do-i-discontinue-a-medication'

  return (
    <PopupModal
      buttonDisabled={!trim(fields.reasonDeleted)}
      buttonIcon="DeleteOutlined"
      buttonStyle={styles.discontinueBtn}
      buttonType="discontinue"
      confirmText="Discontinue"
      footerStyle={styles.footerStyle}
      onConfirm={() => handleSubmit(modalProps)}
      style={styles.popupModal}
      supportLink={
        <Link
          to={supportLinkText}
          target="_blank"
          rel="noreferrer"
          style={styles.supportLink}
        >
          Read more <ExternalLink />
        </Link>
      }
      title="Discontinue Medication"
      titleIcon={<Warning size={24} color="#DE7C12" />}
      {...formProps}
    >
      <Divider style={styles.description} />
      <Text weight="regular">
        Discontinue <strong>{medication.name}</strong> for{' '}
        <strong>
          {resident.name.first} {resident.name.last}
        </strong>
      </Text>
      <br /> <br />
      <Asterix />
      <Text weight="regular">Why is this medication being discontinued?</Text>
      <Suspense fallback={<div>Loading...</div>}>
        <Field
          formId={MODAL_ID}
          keys="reasonDeleted"
          layout="no-label"
          style={styles.reasonField}
          type="textarea"
          values={fields}
        />
      </Suspense>
      <div style={styles.note}>
        <Text level={2}>
          Discontinuing this medication will mean that carers will mean that
          carers will no longer be able to sign for it in the App.
        </Text>
      </div>
    </PopupModal>
  )
}

const MODAL_ID = 'discontinueMed'

const styles = {
  discontinueBtn: {
    margin: 0,
    marginRight: 16
  },
  supportLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap'
  },
  footerStyle: { marginBottom: 16, justifyContent: 'space-between' },
  note: {
    borderRadius: 8,
    border: '1px solid #D9D9D9',
    background: '#FAFAFA',
    padding: 16,
    marginTop: 16,
    color: '#595959'
  },
  popupModal: {
    maxWidth: 630
  },
  reasonField: {
    minHeight: 90
  },
  reasonFieldTitle: {
    fontWeight: 700,
    lineHeight: '22px',
    marginBottom: 8
  },
  subtitle: {
    fontSize: 16,
    fontWeight: 400,
    marginBottom: 16
  },
  description: {
    marginBottom: 16
  }
}

export default Modal(EmarDiscontinueMedModal, MODAL_ID)
